// ============================================================================
// AllocationsTab.jsx
// Where cost lands, as opposed to where people sit.
//
// Three things are true of this data and the layout follows from them:
//
//   Only a minority of workers carry a costing override — roughly 420 of
//   2,500. Everyone else charges to their home cost centre. Any chart of
//   city or line of business therefore describes a slice, and says so, rather
//   than implying the company is 250 people.
//
//   The interesting number is the gap. 107 Governmental Affairs holds 27 heads
//   and almost no cost; 310 Valley Transportation carries more cost than it
//   holds heads. A ranked list of totals buries that, so the difference gets
//   its own panel.
//
//   The detail belongs under the row it explains, not in a drawer over the
//   top of it — comparing two cost centres means having both open.
// ============================================================================

import { useState, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ReferenceLine,
} from 'recharts';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { api } from '../../services/api';
import { c, t, r, card, tbl, chart } from '../../theme';
import LoadingSpinner from '../Shared/LoadingSpinner';
import CostCenterTrend from './CostCenterTrend';

const n2 = (v) => Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const n0 = (v) => Number(v ?? 0).toLocaleString();

export default function AllocationsTab({ data }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const rows = data?.cost_centers || [];

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16, marginBottom: 16 }}>
        <TopCostCenters rows={rows} />
        <GapPanel />
      </div>

      <CostCenterTrend kind="allocations" />

      <DimensionPanel />

      <div style={{ ...card.base, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: c.surfaceSunk }}>
              <th style={{ ...tbl.th, width: 28 }} />
              <th style={tbl.th}>Cost Center</th>
              <th style={tbl.thR}>Allocated</th>
              <th style={tbl.thR}>Charged In</th>
              <th style={tbl.thR}>Own Split</th>
              <th style={tbl.thR} title="Charging to their home cost center">Home</th>
              <th style={{ ...tbl.th, width: 140 }} title="Charged in from elsewhere / own split / home cost center">Mix</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const id = row.cost_center_id || row.raw_cost_center;
              const open = expanded.has(id);
              // Three parts, not two: charged in from elsewhere, own staff
              // whose allocation points back at their own cost centre, and
              // people with no override at all. Only the first moves cost.
              const elsewhere = Number(row.from_elsewhere || 0);
              const own = Number(row.from_allocation || 0) - elsewhere;
              const sits = Number(row.from_default || 0);
              const total = elsewhere + own + sits || 1;
              return (
                <Fragment key={id}>
                  <tr style={{ ...tbl.trHover, background: open ? c.infoTint : undefined }}
                      onClick={() => toggle(id)}>
                    <td style={{ ...tbl.td, color: c.textMuted }}>
                      {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                    </td>
                    <td style={tbl.td}>{row.raw_cost_center}</td>
                    <td style={{ ...tbl.tdR, fontWeight: open ? 600 : 400 }}>{n2(row.allocated_heads)}</td>
                    <td style={{ ...tbl.tdR, color: elsewhere > 0 ? c.text : c.textFaint,
                                 fontWeight: elsewhere > 0 ? 600 : 400 }}>{n2(elsewhere)}</td>
                    <td style={{ ...tbl.tdR, color: c.textMuted }}>{n2(own)}</td>
                    <td style={{ ...tbl.tdR, color: c.textMuted }}>{n2(sits)}</td>
                    <td style={tbl.td}>
                      {/* Charged-in versus home, readable without expanding. */}
                      <div title={`${n2(elsewhere)} charged in from elsewhere · ${n2(own)} own split · ${n2(sits)} home`}
                           style={{ display: 'flex', height: 12, borderRadius: 3, overflow: 'hidden',
                                    background: c.canvas, border: `1px solid ${c.border}` }}>
                        <div style={{ width: `${(elsewhere / total) * 100}%`, background: chart.categorical[1] }} />
                        <div style={{ width: `${(own / total) * 100}%`, background: chart.categorical[0] }} />
                        <div style={{ width: `${(sits / total) * 100}%`, background: chart.sequential[0] }} />
                      </div>
                    </td>
                  </tr>
                  {open && (
                    <tr>
                      <td colSpan={7} style={{ padding: 0, background: c.surfaceSunk, borderBottom: `1px solid ${c.border}` }}>
                        <CostCenterDetail ccId={row.cost_center_id} name={row.raw_cost_center} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Detail, inline under the row it explains ────────────────────────────────
function CostCenterDetail({ ccId, name }) {
  const [view, setView] = useState('elsewhere');
  const { data, isLoading, error } = useQuery({
    queryKey: ['hr-alloc-detail', ccId],
    queryFn: () => api.get(`/hr-headcount/allocations/${ccId}`),
    enabled: !!ccId,
  });

  if (!ccId) return <div style={{ padding: 14, ...t.bodySm, color: c.textMuted }}>
    This cost center is not mapped in the portal, so its detail cannot be listed.
  </div>;
  if (isLoading) return <div style={{ padding: 14 }}><LoadingSpinner /></div>;
  if (error) return <div style={{ padding: 14, ...t.bodySm, color: c.dangerText }}>{error.message}</div>;

  const lines = data?.lines || [];
  // "Charged in" on its own is misleading: most allocation lines point at the
  // worker's own home cost centre, which moves no cost anywhere. In 151 LA
  // North that is 66 lines of which 9 actually come from another cost centre.
  // The nine are what this view is for, so they are the default.
  const elsewhere = lines.filter((l) => !l.defaulted && l.from_elsewhere);
  const ownAllocated = lines.filter((l) => !l.defaulted && !l.from_elsewhere);
  const seated = lines.filter((l) => l.defaulted);
  const shown = view === 'elsewhere' ? elsewhere : view === 'own' ? ownAllocated : seated;
  const sumShare = (rows) => rows.reduce((a, x) => a + Number(x.distribution_percent || 0), 0) / 100;

  return (
    <div style={{ padding: '12px 16px 16px 44px' }}>
      {/* Scrolls inside itself: inline detail that grows without limit pushes
          every other cost centre off the screen, which defeats expanding two
          of them to compare. */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <Pill active={view === 'elsewhere'} onClick={() => setView('elsewhere')}>
          From other cost centers — {n0(elsewhere.length)}
        </Pill>
        <Pill active={view === 'own'} onClick={() => setView('own')}>
          Own staff, split — {n0(ownAllocated.length)}
        </Pill>
        <Pill active={view === 'seated'} onClick={() => setView('seated')}>
          Home cost center — {n0(seated.length)}
        </Pill>
        <span style={{ ...t.micro, color: c.textFaint, marginLeft: 4 }}>
          {n2(sumShare(elsewhere))} of {n2(sumShare(elsewhere) + sumShare(ownAllocated) + seated.length)} charged here
          comes from another cost center
        </span>
      </div>

      {shown.length === 0 ? (
        <div style={{ ...t.bodySm, color: c.textMuted }}>
          {view === 'elsewhere'
            ? `Nothing is charged into ${name} from another cost center — every allocation here belongs to someone whose home cost center this already is.`
            : view === 'own'
              ? `Nobody with ${name} as their home cost center carries a costing override.`
              : `No one has ${name} as their home cost center — every head here is charged in from somewhere else.`}
        </div>
      ) : (
        <div style={{ maxHeight: 340, overflowY: 'auto', border: `1px solid ${c.border}`, borderRadius: r.md, background: c.surface }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ position: 'sticky', top: 0, background: c.surfaceSunk, zIndex: 1 }}>
            <tr>
              <th style={tbl.thSm}>Worker</th>
              <th style={tbl.thSm}>{view === 'seated' ? 'Home cost center' : 'Charged from'}</th>
              <th style={tbl.thSm}>City</th>
              <th style={tbl.thSm}>Line of Business</th>
              <th style={tbl.thRSm}>Share</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((w, i) => (
              <tr key={`${w.employee_id}-${i}`} style={tbl.tr}>
                <td style={tbl.tdSm}>
                  {w.worker_name}
                  {w.worker_status === 'on_leave' && (
                    <span style={{ marginLeft: 6, ...t.micro, color: c.warningText }}>on leave</span>
                  )}
                  <div style={{ ...t.micro, color: c.textFaint }}>{w.employee_id}</div>
                </td>
                <td style={{ ...tbl.tdSm, color: w.from_elsewhere ? c.text : c.textMuted,
                             fontWeight: w.from_elsewhere ? 600 : 400 }}>
                  {w.raw_home_cost_center || '—'}
                </td>
                <td style={{ ...tbl.tdSm, color: c.textMuted }}>{w.city_code || '—'}</td>
                <td style={{ ...tbl.tdSm, color: c.textMuted }}>
                  {w.line_of_business || '—'}
                  {w.resolved_from_home_cc && (
                    <div style={{ ...t.micro, color: c.warningText }}>used home cost center</div>
                  )}
                </td>
                <td style={tbl.tdRSm}>{n2(w.distribution_percent)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

function Pill({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      ...t.micro, padding: '4px 10px', borderRadius: r.pill, cursor: 'pointer',
      border: `1px solid ${active ? c.navy : c.border}`,
      background: active ? c.navy : c.surface,
      color: active ? c.onNavy : c.textMuted,
    }}>{children}</button>
  );
}

// ── Ranked totals ───────────────────────────────────────────────────────────
function TopCostCenters({ rows }) {
  const top = rows.slice(0, 10).map((x) => ({
    name: x.raw_cost_center, allocated: Number(x.allocated_heads || 0),
  }));
  if (!top.length) return null;
  return (
    <div style={{ ...card.base, padding: '16px 18px' }}>
      <div style={{ ...t.label, color: c.textMuted, marginBottom: 2 }}>Largest cost centers</div>
      <div style={{ ...t.bodySm, color: c.textFaint, marginBottom: 10 }}>By allocated headcount</div>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={top} layout="vertical" margin={{ left: 4, right: 16, top: 0, bottom: 0 }}>
          <CartesianGrid stroke={chart.grid} horizontal={false} />
          <XAxis type="number" tick={{ fill: chart.axisText, fontSize: 11 }} tickLine={false} axisLine={false} />
          <YAxis type="category" dataKey="name" width={150}
                 tick={{ fill: chart.axisText, fontSize: 11 }} tickLine={false} axisLine={false} />
          <Tooltip formatter={(v) => [n2(v), 'Allocated']}
                   contentStyle={{ border: `1px solid ${c.border}`, borderRadius: r.md, fontSize: 12 }} />
          <Bar dataKey="allocated" fill={chart.sequential[2]} radius={[0, 3, 3, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── The gap ─────────────────────────────────────────────────────────────────
function GapPanel() {
  const { data, isLoading } = useQuery({
    queryKey: ['hr-alloc-comparison'],
    queryFn: () => api.get('/hr-headcount/allocations/comparison'),
  });
  if (isLoading) return <div style={{ ...card.base, padding: 18 }}><LoadingSpinner /></div>;

  const rows = (data?.cost_centers || []).filter((x) => Number(x.delta) !== 0).slice(0, 10);
  if (!rows.length) {
    return (
      <div style={{ ...card.base, padding: 18 }}>
        <div style={{ ...t.label, color: c.textMuted }}>Cost against heads</div>
        <div style={{ ...t.bodySm, color: c.textMuted, marginTop: 8 }}>
          No cost center differs — every worker charges to their home cost center.
          That is what you would expect before an allocation file has been loaded.
        </div>
      </div>
    );
  }
  const chartRows = rows.map((x) => ({ name: x.raw_cost_center, delta: Number(x.delta) })).reverse();

  return (
    <div style={{ ...card.base, padding: '16px 18px' }}>
      <div style={{ ...t.label, color: c.textMuted, marginBottom: 2 }}>Cost against heads</div>
      <div style={{ ...t.bodySm, color: c.textFaint, marginBottom: 10 }}>
        Right of the line, a cost center carries more cost than it holds people
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={chartRows} layout="vertical" margin={{ left: 4, right: 16, top: 0, bottom: 0 }}>
          <CartesianGrid stroke={chart.grid} horizontal={false} />
          <XAxis type="number" tick={{ fill: chart.axisText, fontSize: 11 }} tickLine={false} axisLine={false} />
          <YAxis type="category" dataKey="name" width={150}
                 tick={{ fill: chart.axisText, fontSize: 11 }} tickLine={false} axisLine={false} />
          <Tooltip formatter={(v) => [`${v > 0 ? '+' : ''}${n2(v)}`, 'Cost minus heads']}
                   contentStyle={{ border: `1px solid ${c.border}`, borderRadius: r.md, fontSize: 12 }} />
          <ReferenceLine x={0} stroke={c.borderStrong} />
          <Bar dataKey="delta" radius={[0, 3, 3, 0]}>
            {chartRows.map((x, i) => (
              <Cell key={i} fill={x.delta > 0 ? chart.categorical[2] : chart.categorical[1]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── City and line of business ───────────────────────────────────────────────
function DimensionPanel() {
  const [by, setBy] = useState('line_of_business');
  const { data, isLoading } = useQuery({
    queryKey: ['hr-alloc-dimensions', by],
    queryFn: () => api.get('/hr-headcount/allocations/dimensions', { params: { by } }),
  });

  // pg returns numeric as a string; Recharts silently plots nothing and still
  // draws the axes, so a broken chart looks like an empty one.
  const dims = (data?.dimensions || []).slice(0, 12)
    .map((d) => ({ ...d, share: Number(d.share) }));
  const unallocated = Number(data?.unallocated || 0);
  const allocated = (data?.dimensions || []).reduce((a, d) => a + Number(d.share), 0);

  return (
    <div style={{ ...card.base, padding: '16px 18px', marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div>
          <div style={{ ...t.label, color: c.textMuted, marginBottom: 2 }}>Split by {by === 'city_code' ? 'city' : 'line of business'}</div>
          {/* These dimensions exist only on an explicit allocation line, so the
              chart covers a slice of the workforce. Saying so is the difference
              between a useful chart and a misleading one. */}
          <div style={{ ...t.bodySm, color: c.textFaint }}>
            Covers the {n2(allocated)} with a costing override — the other {n0(unallocated)} charge
            to their home cost center and carry no {by === 'city_code' ? 'city' : 'line of business'}.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <Pill active={by === 'line_of_business'} onClick={() => setBy('line_of_business')}>Line of business</Pill>
          <Pill active={by === 'city_code'} onClick={() => setBy('city_code')}>City</Pill>
        </div>
      </div>

      {isLoading ? <LoadingSpinner /> : (
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={dims} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
            <CartesianGrid stroke={chart.grid} vertical={false} />
            <XAxis dataKey="label" tick={{ fill: chart.axisText, fontSize: 10 }} tickLine={false}
                   axisLine={false} angle={-35} textAnchor="end" interval={0} height={86} />
            <YAxis tick={{ fill: chart.axisText, fontSize: 11 }} tickLine={false} axisLine={false} width={44} />
            <Tooltip formatter={(v, n, p) => [`${n2(v)} (${p.payload.lines} lines)`, 'Allocated']}
                     contentStyle={{ border: `1px solid ${c.border}`, borderRadius: r.md, fontSize: 12 }} />
            <Bar dataKey="share" fill={chart.categorical[0]} radius={[3, 3, 0, 0]} maxBarSize={46} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
